import {initialState, ReportsState} from "src/services/reports/ReportsState";
import React, {useEffect} from "react";
import TPEAction from "src/models/common/TPEAction";
import ServiceCollection from "src/services/ServiceCollection";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import {reportsReducer} from "src/services/reports/ReportsReducer";
import {Box, Button, Container, Pagination, SpaceBetween} from "@amzn/awsui-components-react";
import {Grid} from "@amzn/awsui-components-react/polaris";
import {CLI_EXECUTION_RECORDS_PAGE_LIMIT} from "src/utils/constants";
import {
    CLIExecutionRecord,
    GetCLIExecutionRecordsRequest
} from "src/models/reports/GetCLIExecutionRecords";
import JEStatusDashboardFilterSelector from "src/components/reports/jeStatusDashboard/JEStatusDashboardFilterSelector";
import JEStatusDashboardTableGrid from "src/components/reports/jeStatusDashboard/JEStatusDashboardTableGrid";
import { parse, format } from 'date-fns';
import {DownloadCLIExecutionRecordsRequest} from "src/models/reports/DownloadCLIExecutionRecords";
import StringUtils from "src/utils/stringUtils";
import ArrayUtils from "src/utils/arrayUtils";

export type ContextType = {
    state: ReportsState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
}

const JEStatusDashboardProvider = (props: {
    state: ReportsState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    children: JSX.Element
}) => {
    const {state, dispatch, services, children} = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services
    }), [state, dispatch]);

    return (
        <JEStatusDashboardContext.Provider value={providerValue}>
            {children}
        </JEStatusDashboardContext.Provider>
    );
}

export const JEStatusDashboardContext = React.createContext(null as unknown as ContextType);

export default function JEStatusDashboardView(props: { services: ServiceCollection }) {
    const {services} = props;
    const [state, dispatch] = useReducerWithLogger(reportsReducer, initialState);
    const [resetNeededSignal, setResetNeededSignal] = React.useState(null as unknown as string);
    const [cliExecutionRecordList, setCLIExecutionRecordList] = React.useState([] as CLIExecutionRecord[]);
    const [cache, setCache] = React.useState<{[key: number]: CLIExecutionRecord[]}>({})
    const [selectedAccountingPeriod, setSelectedAccountingPeriod] = React.useState(null as null | any);
    const [selectedPage, setSelectedPage] = React.useState(1);
    const [loadedPage, setLoadedPage] = React.useState(1);
    const [searchResultsIsVisible, setSearchResultsIsVisible] = React.useState(false);
    const [selectedGroupNumber, setSelectedGroupNumber] = React.useState(null as null | any);
    const [selectedJEStatus, setSelectedJEStatus] = React.useState(null as null | any);
    const [selectedApttusWorkbookName, setSelectedApttusWorkbookName] = React.useState(null as null | any);
    const [cliExecutionRecordsRequest, setCLIExecutionRecordsRequest] = React.useState(null as unknown as GetCLIExecutionRecordsRequest);
    const [downloadRecordsRequest, setDownloadRecordsRequest] = React.useState(null as unknown as DownloadCLIExecutionRecordsRequest);
    const [cliExecutionRecordsResponse, cliExecutionRecordsLoading, cliExecutionRecordsError] = services.reportsService.getCLIExecutionRecords(cliExecutionRecordsRequest);
    const [downloadCLIExecutionRecordsUrl, downloadCLIExecutionRecordsLoading, downloadCLIExecutionRecordsError] = services.reportsService.downloadCLIExecutionRecords(downloadRecordsRequest);

    useEffect(() => {
        if (!cliExecutionRecordsError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(cliExecutionRecordsError);
    }, [cliExecutionRecordsError])

    useEffect(() => {
        if (cliExecutionRecordsResponse == null) {
            return;
        }
        setCache({ ...cache, [selectedPage]: cliExecutionRecordsResponse.cliExecutionRecordsList})
        setCLIExecutionRecordList(cliExecutionRecordsResponse.cliExecutionRecordsList)
    }, [cliExecutionRecordsResponse])

    useEffect(() => {
        if (!downloadCLIExecutionRecordsError) {
            return;
        }
        services.messageService.showErrorAutoDismissBanner(downloadCLIExecutionRecordsError);
    }, [downloadCLIExecutionRecordsError]);

    useEffect(() => {
        if (StringUtils.isNullOrEmpty(downloadCLIExecutionRecordsUrl)) {
            return;
        }
        window.open(downloadCLIExecutionRecordsUrl);
    }, [downloadCLIExecutionRecordsUrl])

    const onDownloadClicked = () => {
        if (!!selectedAccountingPeriod) {
            const formattedAccountingDate = format(parse(selectedAccountingPeriod, 'MMM-yy', new Date()), 'yyyyMM');
            setDownloadRecordsRequest({
                periodId: formattedAccountingDate
            })
        }
    }

    const fetchCLIExecutionRecords = (startKey : string | null) => {
        if (!!selectedAccountingPeriod) {
            const formattedAccountingDate = format(parse(selectedAccountingPeriod, 'MMM-yy', new Date()), 'yyyyMM');
            setCLIExecutionRecordsRequest({
                periodId: formattedAccountingDate,
                groupNumber: selectedGroupNumber,
                jeStatus: selectedJEStatus,
                apttusWorkbookName: selectedApttusWorkbookName,
                limit: CLI_EXECUTION_RECORDS_PAGE_LIMIT,
                startKey: startKey
            })
        }
    }

    const handleRefresh = () => {
        setCache({})
        setLoadedPage(1);
        setSelectedPage(1);
        setSearchResultsIsVisible(true)
        fetchCLIExecutionRecords(null)
    };

    const onResetClicked = () => {
        setCache({})
        setSearchResultsIsVisible(false)
        setSelectedAccountingPeriod(null)
        setSelectedGroupNumber(null)
        setSelectedJEStatus(null)
        setSelectedApttusWorkbookName(null)
        setResetNeededSignal(new Date().toString());
    };

    return (<JEStatusDashboardProvider services={services} state={state} dispatch={dispatch}>
        <Container className="polaris-content-container">
            <SpaceBetween direction="vertical" size="xxl">
                <Grid gridDefinition={[{colspan: 8}, {colspan: 4}]}>
                    <JEStatusDashboardFilterSelector
                        selectedAccountingPeriod={selectedAccountingPeriod}
                        selectedGroupNumber={selectedGroupNumber}
                        selectedJEStatus={selectedJEStatus}
                        selectedApttusWorkbookName={selectedApttusWorkbookName}
                        services={services}
                        onAccountingPeriodSelected={(periodId) => setSelectedAccountingPeriod(periodId)}
                        onGroupNumberSelected={(groupNumber) => setSelectedGroupNumber(groupNumber)}
                        onJEStatusSelected={(jeStatus) => setSelectedJEStatus(jeStatus)}
                        onApttusWorkbookNameSelected={(apttusWorkbookName) => setSelectedApttusWorkbookName(apttusWorkbookName)}
                    />
                    <Box float="right" padding={{top: "xl"}}>
                        <SpaceBetween direction="horizontal" size="s">
                            <Button onClick={onResetClicked}>Reset</Button>
                            <Button
                                className={"applyCriteriaButton"}
                                onClick={handleRefresh}
                                disabled = { (!selectedAccountingPeriod) || (cliExecutionRecordsLoading) }
                                variant="primary"
                            >
                                {cliExecutionRecordsLoading ? 'Applying Criteria...' : 'Apply Criteria' }
                            </Button>
                            <Button
                                className={"downloadButton"}
                                onClick={onDownloadClicked}
                                disabled = { (!selectedAccountingPeriod) || (downloadCLIExecutionRecordsLoading) }
                                variant={"primary"}
                            >
                                { downloadCLIExecutionRecordsLoading ? 'Downloading...' : 'Download' }
                            </Button>
                        </SpaceBetween>
                    </Box>
                </Grid>
                {
                    searchResultsIsVisible &&
                    <div>
                        <div className="tableHeader">
                            <h3>CLI Journal Entry Status</h3>
                            {!ArrayUtils.isNullOrEmpty(cliExecutionRecordsResponse?.cliExecutionRecordsList) &&
                                <Box float="right">
                                    <Pagination
                                        currentPageIndex={selectedPage}
                                        pagesCount={loadedPage}
                                        onNextPageClick={({ detail }) => {
                                            setSelectedPage(detail.requestedPageIndex);
                                            if (cache.hasOwnProperty(detail.requestedPageIndex)) {
                                                setCLIExecutionRecordList(cache[detail.requestedPageIndex]);
                                            } else if (cliExecutionRecordsResponse.lastEvaluatedKey) {
                                                setLoadedPage(loadedPage + 1);
                                                fetchCLIExecutionRecords(cliExecutionRecordsResponse.lastEvaluatedKey);
                                            }
                                        }}
                                        onChange={({ detail }) => {
                                            setSelectedPage(detail.currentPageIndex)
                                            setCLIExecutionRecordList(cache[detail.currentPageIndex])
                                        }}
                                        onPreviousPageClick={({ detail }) => {
                                            setSelectedPage(detail.requestedPageIndex)
                                            setCLIExecutionRecordList(cache[detail.requestedPageIndex])
                                        }}
                                        openEnd={!!cliExecutionRecordsResponse.lastEvaluatedKey}
                                    />
                                </Box>
                            }
                        </div>
                        <div className="tableContainer">
                            <JEStatusDashboardTableGrid
                                key="jePostedDashboardTableGrid"
                                loading={cliExecutionRecordsLoading}
                                cliExecutionRecordTables={cliExecutionRecordList}
                            />
                        </div>
                    </div>
                }
            </SpaceBetween>
        </Container>
    </JEStatusDashboardProvider>)

}